import { useDispatch } from 'react-redux';

// material-ui
import { Button, DialogActions, DialogContent, DialogTitle, Divider, InputLabel, Stack, TextField, Typography } from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// third-party
import * as Yup from 'yup';
import { Form, FormikProvider, useFormik } from 'formik';

// project imports
import { openSnackbar } from 'store/reducers/snackbar';

// assets
import {
  Integration,
  MerchantCashAdvance,
  useNonWorkingDaysQuery,
  RecurrencyState,
  useSaveRecurrencyMutation,
  Recurrency,
  MerchantCashAdvanceDocument,
  McaRecurrenciesDocument,
  GetMcaIntegrationsWithTransactionsCountDocument,
  IntegrationType,
  useIntegrationsQuery,
  Company,
  Transaction,
  McaFeeTransactionsDocument
} from '../../graphql/types';
import { v4 as uuid4 } from 'uuid';
import React, { useMemo, useState } from 'react';
import useConfig from '../../hooks/useConfig';
import PercentFormatterWithoutPrefix from '../../components/input-formatters/PercentFormatterWithoutPrefix';
import CurrencyFormatterWithoutPrefix from '../../components/input-formatters/CurrencyFormatterWithoutPrefix';
import { IntegrationAutocomplete } from '../integrations/IntegrationAutocomplete';
import { MerchantBankAccountInput } from './MerchantBankAccountInput';
import AssetLogo from '../AssetLogo';
import { isValidRoutingNumber } from '../../utils/bankAccounts';
import { FlagOutlined } from '@ant-design/icons';
import AddMerchantCashAdvanceConfirm from './AddMerchantCashAdvanceConfirm';
import Decimal from 'decimal.js';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { getProcessorStartDate, getNextBusinessDay } from '../../utils/getProcessorStartDate';
import { getErrorMessage, requireMutationData } from '../../utils/graphqlMutationResult';
dayjs.extend(utc);
dayjs.extend(isSameOrAfter);

// ==============================|| RECURRENCE ADD / EDIT / DELETE ||============================== //

export interface RecurrencyValues {
  description: string;
  startDate: Date;
  integration: Integration;
  accountNumber: string;
  routingNumber: string;
  amount: number;
  dailyPayment: number;
  term: number;
  merchant: Company;
}

export interface Props {
  isFeeRecurrence?: boolean;
  transaction?: Transaction;
  mca: MerchantCashAdvance;
  handleClose: () => void;
  handleSave: (recurrency: Recurrency, openRecurrencyAchDetails: boolean) => void;
}

const AddRecurrence = ({ mca, handleClose, transaction, handleSave, isFeeRecurrence }: Props) => {
  const { mode } = useConfig();
  const dispatch = useDispatch();
  const [saveRecurrency] = useSaveRecurrencyMutation();
  const [confirmAccountOpen, setConfirmAccountOpen] = useState<boolean>(false);
  const [finalCheckPassed, setFinalCheckPassed] = useState<boolean>(false);
  const { data: nonWorkingDaysData } = useNonWorkingDaysQuery({
    variables: {},
    fetchPolicy: 'cache-and-network'
  });
  const nonWorkingDays = useMemo(() => nonWorkingDaysData?.nonWorkingDays || [], [nonWorkingDaysData]);
  const { data: integrationsData } = useIntegrationsQuery({
    variables: {},
    fetchPolicy: 'network-only'
  });
  const [startDateChanged, setStartDateChanged] = useState<boolean>(false);

  const today = dayjs().set('hour', 0).set('minute', 0).set('second', 0).toDate();
  const tomorrow = dayjs().set('hour', 0).set('minute', 0).set('second', 0).add(1, 'day').toDate();
  const nextBusinessDay = getNextBusinessDay(tomorrow, nonWorkingDays);
  const getStartDateColor = (startDate: Date | null): string => {
    if (startDate && dayjs(startDate).isSame(today, 'date')) {
      return '#FF7A45';
    } else if (startDate && dayjs(startDate).isSame(nextBusinessDay, 'date')) {
      return '#52C41A';
    }
    return 'inherit';
  };
  const RecurrenceSchema = Yup.object().shape({
    dailyPayment: Yup.number().required('Daily payment amount is required'),
    term: Yup.number().min(1, 'Term is required').required('Term is required'),
    integration: Yup.object().shape({
      id: Yup.string().max(255).required('Platform is required')
    }).required('Platform is required'),
    accountNumber: Yup.string().when(['integration'], {
      is: (integration: Integration) => true,
      then: () =>
        Yup.string()
          .matches(/^[0-9]+$/, 'Account number is invalid')
          .min(5, 'Account number is invalid')
          .max(17, 'Account number is invalid')
          .required('Account number is required')
    }),
    routingNumber: Yup.string().when(['integration'], {
      is: (integration: Integration) => true,
      then: () =>
        Yup.string()
          .matches(/[0-9]+/gi, 'Routing number is invalid')
          .test('routing-number-validation', 'Checksum of routing number is invalid', isValidRoutingNumber)
          .required('Routing number is required')
    }),
    startDate: Yup.date()
      .notRequired()
      .when(['integration'], {
        is: (integration: Integration | null) =>
          !!integration?.id &&
          [IntegrationType.GoAch, IntegrationType.GoAchBot, IntegrationType.DevNull].includes(
            integrationsData?.integrations.data.find(({ id }) => id === integration.id)?.type || IntegrationType.DevNull
          ),
        then: () =>
          Yup.date()
            .typeError('Must be a valid date')
            .test('is-today-or-later', 'Must be current business day or later', function (value) {
              const selectedDate = dayjs(value);
              const minDate = dayjs(getNextBusinessDay(today, nonWorkingDays));
              return selectedDate.isSameOrAfter(minDate, 'day');
            })
            .required('Start date is required')
      })
      .when(['integration'], {
        is: (integration: Integration | null) =>
          !!integration?.id &&
          [IntegrationType.AchProc, IntegrationType.ClearAchBot].includes(
            integrationsData?.integrations.data.find(({ id }) => id === integration.id)?.type || IntegrationType.DevNull
          ),
        then: () =>
          Yup.date()
            .typeError('Must be a valid date')
            .test('is-tomorrow-or-later', 'Must be next business day or later', function (value) {
              const selectedDate = dayjs(value);
              const minDate = dayjs(nextBusinessDay);
              return selectedDate.isSameOrAfter(minDate, 'day');
            })
            .required('Start date is required')
      })
  });

  const getInitialValues = (transaction: Transaction | undefined, mca: MerchantCashAdvance, isFeeRecurrence: boolean): RecurrencyValues => {
    const integration = transaction?.integration || ({ id: '' } as Integration);
    const newEntity = {
      amount: isFeeRecurrence ? 999 : 100,
      dailyPayment: isFeeRecurrence ? 99 : 100,
      startDate: getProcessorStartDate(
        integration,
        integrationsData?.integrations.data.find(({ id }) => id === integration?.id)?.type,
        undefined,
        nonWorkingDays,
        startDateChanged
      ),
      description: isFeeRecurrence ? 'CRM FEE' : 'MCA PMT',
      integration: { id: '' } as Integration,
      term: isFeeRecurrence ? 1 : 10,
      accountNumber: '',
      routingNumber: '',
      merchant: mca.merchant
    };

    if (transaction) {
      newEntity.dailyPayment = transaction.amount;
      newEntity.integration = transaction.integration || ({ id: '' } as Integration);
    }
    return newEntity;
  };

  const rowSx = {
    backgroundColor: mode === 'dark' ? '#3B414F' : '#e6ebf1',
    padding: 2,
    borderBottom: '1px solid',
    borderColor: '#656974'
  };

  const formik = useFormik({
    initialValues: getInitialValues(transaction, mca, !!isFeeRecurrence),
    validationSchema: RecurrenceSchema,
    onSubmit: async (values, { setSubmitting }) => {
      if (!finalCheckPassed) {
        return setConfirmAccountOpen(true);
      }
      try {
        const res = await saveRecurrency({
          variables: {
            id: uuid4(),
            mcaId: mca.id,
            integrationId: values.integration.id,
            dto: {
              state: RecurrencyState.New,
              dailyPayment: new Decimal(values.dailyPayment).toNumber(),
              amount: values.dailyPayment * values.term,
              description: values.description,
              term: new Decimal(values.term).toNumber(),
              startDate: dayjs(values.startDate).set('hour', 12).set('minute', 0).set('second', 0).format('YYYY-MM-DDTHH:mm:ss[Z]'),
              routingNumber: values.routingNumber,
              accountNumber: values.accountNumber
            }
          },
          refetchQueries: [
            { query: MerchantCashAdvanceDocument, variables: { id: mca?.id } },
            { query: McaRecurrenciesDocument, variables: { id: mca?.id } },
            { query: McaFeeTransactionsDocument, variables: { id: mca?.id } },
            { query: GetMcaIntegrationsWithTransactionsCountDocument, variables: { mcaId: mca?.id } }
          ], awaitRefetchQueries: true
        });
        const recurrency = requireMutationData(res, (data) => data?.saveRecurrency, 'Recurrence was not saved.');
        handleSave(recurrency as Recurrency, true);
        dispatch(
          openSnackbar({
            open: true,
            message: 'Recurrence saved successfully.',
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: true
          })
        );
      } catch (error) {
        dispatch(
          openSnackbar({
            open: true,
            message: getErrorMessage(error, 'Failed to save recurrence.'),
            variant: 'alert',
            alert: {
              color: 'error'
            },
            close: true
          })
        );
      }
    }
  });

  const { errors, touched, values, handleSubmit, isSubmitting, getFieldProps, handleBlur, setFieldValue, submitForm } = formik;

  return (
    <>
      <AddMerchantCashAdvanceConfirm
        open={confirmAccountOpen}
        handleConfirmDone={(res) => {
          setConfirmAccountOpen(false);
          setFinalCheckPassed(res);
          if (res) {
            void submitForm();
          }
        }}
        data={values}
      />
      <FormikProvider value={formik}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <DialogTitle> {!isFeeRecurrence ? 'New Recurrence' : 'New Fee Transaction'}</DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 2.5 }}>
              <Stack direction={'row'}>
                <Stack
                  sx={{
                    width: 233,
                    minWidth: 233,
                    maxWidth: 233,
                    borderRadius: 0,
                    borderLeft: '1px solid',
                    borderBottom: '1px solid',
                    borderTop: '1px solid',
                    borderRight: '1px solid',
                    borderColor: '#656974'
                  }}
                >
                  <Stack alignItems="center" sx={rowSx}>
                    <InputLabel required={true} htmlFor="rec-entity-startDate" sx={{ color: '#B4ADE5' }}>
                      Start Date
                    </InputLabel>
                  </Stack>
                  <Stack alignItems="center" sx={{ padding: 2 }}>
                    <DesktopDatePicker
                      value={values.startDate}
                      onChange={(value) => {
                        setStartDateChanged(true);
                        setFieldValue('startDate', dayjs(value).toDate() || nextBusinessDay);
                      }}
                      disablePast={true}
                      shouldDisableDate={(day) => {
                        if (nonWorkingDays.some((nwd) => dayjs(nwd.date).isSame(dayjs(day), 'day'))) {
                          return true;
                        }
                        return [6, 0].includes(day.getDay());
                      }}
                      slotProps={{
                        textField: {
                          sx: {
                            width: 150,
                            svg: { color: getStartDateColor(values.startDate) },
                            input: { color: getStartDateColor(values.startDate) },
                            label: { color: getStartDateColor(values.startDate) }
                          },
                          size: 'small',
                          error: Boolean(touched.startDate && errors.startDate),
                          helperText: touched.startDate && (errors.startDate ? <>{errors.startDate}</> : ''),
                          name: 'startDate',
                          onBlur: handleBlur
                        }
                      }}
                    />
                  </Stack>
                </Stack>
                <Stack
                  sx={{
                    width: 233,
                    minWidth: 233,
                    maxWidth: 233,
                    borderRadius: 0,
                    borderBottom: '1px solid',
                    borderRight: '1px solid',
                    borderTop: '1px solid',
                    borderColor: '#656974'
                  }}
                >
                  <Stack alignItems="center" sx={rowSx}>
                    <InputLabel required={true} htmlFor="rec-entity-term" sx={{ color: '#36CFC9' }}>
                      Term
                    </InputLabel>
                  </Stack>
                  <Stack alignItems="center" sx={{ padding: 2 }}>
                    <TextField
                      disabled={isFeeRecurrence}
                      size={'small'}
                      sx={{ width: 120 }}
                      id="rec-entity-term"
                      placeholder="Enter Term"
                      {...getFieldProps('term')}
                      error={Boolean(touched.term && errors.term)}
                      helperText={touched.term && errors.term}
                      InputProps={{
                        endAdornment: 'D',
                        inputComponent: PercentFormatterWithoutPrefix as any,
                        inputProps: { min: 1 }
                      }}
                    />
                  </Stack>
                </Stack>
                <Stack
                  sx={{
                    width: 234,
                    minWidth: 234,
                    maxWidth: 234,
                    borderRadius: 0,
                    borderBottom: '1px solid',
                    borderTop: '1px solid',
                    borderRight: '1px solid',
                    borderColor: '#656974'
                  }}
                >
                  <Stack alignItems="center" sx={rowSx}>
                    <InputLabel required={true} htmlFor="rec-entity-dailyPayment" sx={{ color: '#36CFC9' }}>
                      Pmt
                    </InputLabel>
                  </Stack>
                  <Stack alignItems="center" sx={{ padding: 2 }}>
                    <TextField
                      size={'small'}
                      sx={{ width: 160 }}
                      id="rec-entity-dailyPayment"
                      placeholder="Amount"
                      {...getFieldProps('dailyPayment')}
                      error={Boolean(touched.dailyPayment && errors.dailyPayment)}
                      helperText={touched.dailyPayment && errors.dailyPayment}
                      InputProps={{
                        startAdornment: '$',
                        inputComponent: CurrencyFormatterWithoutPrefix as any,
                        inputProps: { min: 1 }
                      }}
                    />
                  </Stack>
                </Stack>
              </Stack>
              <Stack direction={'row'} sx={{ pb: 2 }}>
                <Stack
                  sx={{
                    width: 350,
                    minWidth: 350,
                    maxWidth: 350,
                    borderRadius: 0,
                    borderLeft: '1px solid',
                    borderBottom: '1px solid',
                    borderRight: '1px solid',
                    borderColor: '#656974'
                  }}
                >
                  <Stack spacing={0.25}>
                    <Stack pt={2} height={80} spacing={2}>
                      <IntegrationAutocomplete
                        id={'rec-entity-integration'}
                        formik={formik}
                        disabled={false}
                        onValueChanged={(integration) => {
                          if (!startDateChanged) {
                            const integrationType = integrationsData?.integrations.data.find(({ id }) => id === integration?.id)?.type;
                            const newStartDate = getProcessorStartDate(
                              integration,
                              integrationType,
                              values.startDate,
                              nonWorkingDays,
                              startDateChanged
                            );
                            if (newStartDate) {
                              setFieldValue('startDate', newStartDate);
                            }
                          }
                        }}
                      />
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="flex-start" mt={1} padding={2}>
                      <Stack direction={'row'} spacing={0} mt={1} width="42px" justifyContent="flex-start">
                        <AssetLogo logo={mca?.merchant?.logo} name={mca.merchant.legalName || ''} size={'md'} />
                      </Stack>
                      <Stack spacing={0.1} alignItems="flex-start">
                        <Typography color="#979797" textAlign="left" sx={{ margin: 0 }}>
                          Legal Name
                        </Typography>
                        <Typography textAlign="left" sx={{ margin: 0 }}>
                          {mca.merchant.legalName}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                </Stack>
                <Stack
                  sx={{
                    width: 350,
                    minWidth: 350,
                    maxWidth: 350,
                    borderRadius: 0,
                    borderBottom: '1px solid',
                    borderRight: '1px solid',
                    borderColor: '#656974'
                  }}
                >
                  <Stack spacing={0.25}>
                    <Stack sx={{ padding: 2 }} spacing={2}>
                      <MerchantBankAccountInput
                        merchant={mca.merchant}
                        formik={formik}
                        isCreating={true}
                        accountNumber={transaction?.account || undefined}
                      />
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  color="error"
                  onClick={() => {
                    handleClose();
                  }}
                >
                  Cancel
                </Button>
                <Button startIcon={<FlagOutlined />} type="submit" variant="contained" disabled={isSubmitting}>
                  {!isFeeRecurrence ? 'Start Recurrence' : 'Start Fee Transaction'}
                </Button>
              </Stack>
            </DialogActions>
          </Form>
        </LocalizationProvider>
      </FormikProvider>
    </>
  );
};

export default AddRecurrence;
